/**
 * Referrals + mobile QR CTA — final two sections of the Loyalty page.
 */
import { ArrowRight, Sparkles } from "lucide-react";

const STATS = [
    { v: "500", t: "Earn rate", s: "pts per $100 they spend" },
    { v: "$25", t: "That's worth", s: "off for you, every $100" },
    { v: "∞", t: "Limit", s: "unlimited friends, unlimited points" },
];

const STEPS = [
    "Sign in with your mobile to unlock your personal referral link.",
    "Share it via text, social, or DM — your friend joins and shops.",
    "Points hit your account automatically and stack with your tier.",
];

export default function LoyaltyReferrals({ onOpenModal }) {
    return (
        <>
            <section
                data-testid="loyalty-referrals"
                className="bg-zen-bg border-b border-zen-line"
            >
                <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-12">
                    <div className="mb-6">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                            Refer a friend
                        </p>
                        <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-zen-ink leading-tight tracking-tight">
                            Earn together.
                        </h2>
                        <p className="text-sm sm:text-base text-zen-ink-soft mt-5 max-w-2xl leading-relaxed">
                            Share your ZenCircle referral link with anyone 21+. Every
                            time they shop, points hit your account automatically — on
                            top of your tier multiplier.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                        {STATS.map((s) => (
                            <div
                                key={s.t}
                                className="p-6 border border-zen-line bg-zen-surface"
                            >
                                <p className="text-[10px] uppercase tracking-[0.22em] text-zen-olive mb-2">
                                    {s.t}
                                </p>
                                <p className="font-serif font-light text-5xl text-zen-ink leading-none mb-3">
                                    {s.v}
                                </p>
                                <p className="text-xs text-zen-ink-soft leading-relaxed">
                                    {s.s}
                                </p>
                            </div>
                        ))}
                    </div>
                    <ol className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
                        {STEPS.map((step, i) => (
                            <li
                                key={i}
                                className="p-5 border-l-2 border-zen-olive bg-zen-bg"
                            >
                                <p className="font-serif text-2xl text-zen-olive font-light leading-none mb-3">
                                    {String(i + 1).padStart(2, "0")}
                                </p>
                                <p className="text-sm text-zen-ink leading-relaxed">
                                    {step}
                                </p>
                            </li>
                        ))}
                    </ol>
                    <button
                        onClick={onOpenModal}
                        data-testid="loyalty-referral-cta"
                        className="bg-zen-ink text-zen-cream px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-olive-dark transition-colors inline-flex items-center gap-2"
                    >
                        Unlock my referral link <ArrowRight size={14} />
                    </button>
                    <p className="text-xs text-zen-ink-soft mt-4 max-w-2xl leading-relaxed">
                        Referrals must opt in to text messages. Points apply to
                        qualifying purchases at ZenZest Queens & Staten Island.
                    </p>
                </div>
            </section>

            <section className="bg-zen-surface">
                <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                            One tap to join
                        </p>
                        <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-zen-ink leading-tight tracking-tight mb-5">
                            Skip the punch card.
                        </h2>
                        <p className="text-sm sm:text-base text-zen-ink-soft max-w-xl mb-7 leading-relaxed">
                            ZenCircle lives in your phone. Sign up once, scan at the
                            counter, and your points stack automatically every visit. No
                            app to download.
                        </p>
                        <a
                            href="https://3609.aiqapps.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="loyalty-mobile-cta"
                            className="bg-zen-ink text-zen-cream px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-olive-dark transition-colors inline-flex items-center gap-2"
                        >
                            <Sparkles size={13} /> Open ZenCircle
                        </a>
                        <p className="text-xs text-zen-ink-soft mt-3">
                            Or visit{" "}
                            <a
                                href="https://3609.aiqapps.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline underline-offset-2 hover:text-zen-olive"
                            >
                                3609.aiqapps.com
                            </a>
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex justify-center lg:justify-end">
                        <div className="p-6 bg-zen-bg border border-zen-line text-center">
                            <img
                                src="https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=https%3A%2F%2F3609.aiqapps.com%2F&color=1F2418&bgcolor=FBFAF3&margin=10"
                                alt="Scan to open ZenCircle"
                                width={220}
                                height={220}
                                className="mx-auto"
                            />
                            <p className="text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft mt-4">
                                Scan to join
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
